import React, { useState, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View, Platform, Pressable, ActivityIndicator, NativeModules } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store';
import { API_HOST, GOOGLE_OAUTH_CLIENT_ID_WEB, GOOGLE_OAUTH_CLIENT_ID_IOS, APPLE_SSO_CLIENT_ID } from '@/constants/config';
import { getClientInfo } from '@/constants/utils/analytics';
import { postSSOLogin, handleSSOResponse } from '@/constants/utils/sso';
import Text from '@/components/Typography/Text';
import StatusContainer from '@/components/StatusContainer';
import Svg, { Path } from 'react-native-svg';
import { pendingReferral } from '@/lib/pendingReferral';
import GoogleWebSignInButton from './GoogleWebSignInButton';

const isWeb = Platform.OS === 'web';
const isIos = Platform.OS === 'ios';
const isAndroid = Platform.OS === 'android';
const isMacOS = Platform.OS === 'macos';
const isAppleNative = isIos || isMacOS;
// @react-native-google-signin/google-signin only ships an RNGoogleSignin
// TurboModule for iOS and Android. Importing it on macOS triggers
// `TurboModuleRegistry.getEnforcing('RNGoogleSignin')` and crashes the app,
// so we gate every require() behind this flag and use MacGoogleAuth instead.
const supportsGoogleSdk = isIos || isAndroid;

if (supportsGoogleSdk) {
  try {
    const { GoogleSignin } = require('@react-native-google-signin/google-signin');
    GoogleSignin.configure({
      iosClientId: GOOGLE_OAUTH_CLIENT_ID_IOS,
      webClientId: GOOGLE_OAUTH_CLIENT_ID_WEB,
    });
  } catch (_) {}
}

const GoogleLogo = ({ size = 20 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <Path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <Path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <Path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </Svg>
);

const AppleLogo = ({ size = 20, color = '#000' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.53-3.23 0-1.44.62-2.2.44-3.06-.4C3.79 16.17 4.36 9.53 8.82 9.28c1.28.06 2.15.72 2.9.75.99-.2 1.94-.89 3.01-.8 1.28.1 2.24.63 2.87 1.6-2.63 1.56-2 5 .82 5.97-.62 1.65-1.43 3.26-2.37 4.48zM12.59 9.16c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill={color} />
  </Svg>
);

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: 10,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginVertical: 8,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 16,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '500',
  },
});

// ──────────────────────────────────────────────
// Web: Apple Sign-In JS
// ──────────────────────────────────────────────
function useAppleWebSSO({ onResult }) {
  useEffect(() => {
    if (!isWeb) return;

    const APPLE_CLIENT_ID = APPLE_SSO_CLIENT_ID;
    if (!APPLE_CLIENT_ID) return;

    // Store callback globally
    window.__gabAppleSSOCallback = onResult;

    if (document.getElementById('apple-signin-script')) return;

    const script = document.createElement('script');
    script.id = 'apple-signin-script';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.AppleID?.auth?.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: window.location.origin,
        usePopup: true,
      });
    };
    document.head.appendChild(script);
  }, [onResult]);
}

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────
const SSOButtons = ({ onSuccess, mode = 'login', onIdentity, providers, redirectAfterSuccess = true }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const setAuthData = useAuthStore(state => state.setAuthData);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const currentMacSignIn = useCallback(expected => !expected || (mounted.current
    && useAuthStore.getState().token === expected.token
    && useAuthStore.getState().user?.id === expected.userId), []);

  const handleGoogleIdToken = useCallback(async (idToken) => {
    try {
      setLoading('google');
      setError('');
      if (onIdentity) return await onIdentity({ provider: 'google', idToken });
      const clientInfo = getClientInfo();
      const referralCode = await pendingReferral.read();
      const data = await postSSOLogin('/json/auth/login/google', {
        idToken,
        clientInfo,
        ...(referralCode && { referralCode }),
      });
      await handleSSOResponse(data, setAuthData, onSuccess, 'google', { redirectAfterSuccess });
      if (data.tokens?.access) await pendingReferral.clear(referralCode);
    } catch (err) {
      setError(err.message || t('auth.sso.errors.googleFailed'));
    } finally {
      setLoading(null);
    }
  }, [setAuthData, onSuccess, onIdentity, t, redirectAfterSuccess]);

  const handleGooglePress = useCallback(async () => {
    if (loading) return;

    // macOS: use the native MacGoogleAuth bridge
    // (ASWebAuthenticationSession + OAuth code+PKCE) since
    // @react-native-google-signin/google-signin has no macOS support.
    if (isMacOS) {
      try {
        setLoading('google');
        setError('');

        const credential = await NativeModules.MacGoogleAuth?.signIn({
          iosClientId: GOOGLE_OAUTH_CLIENT_ID_IOS,
          scopes: 'openid email profile',
        });
        if (!credential?.idToken) {
          throw new Error('No ID token received from Google');
        }
        await handleGoogleIdToken(credential.idToken);
      } catch (err) {
        if (err?.code !== 'ERR_REQUEST_CANCELED') {
          setError(err.message || t('auth.sso.errors.googleFailed'));
        }
      } finally {
        setLoading(null);
      }
      return;
    }

    // Native (iOS/Android): use @react-native-google-signin/google-signin
    try {
      setLoading('google');
      setError('');

      const { GoogleSignin } = require('@react-native-google-signin/google-signin');

      await GoogleSignin.configure({
        iosClientId: GOOGLE_OAUTH_CLIENT_ID_IOS,
        webClientId: GOOGLE_OAUTH_CLIENT_ID_WEB,
      });

      if (Platform.OS === 'android') {
        await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
      }
      const signInResult = await GoogleSignin.signIn();
      if (signInResult?.type === 'cancelled') {
        return;
      }
      if (signInResult?.type !== 'success' || !signInResult.data) {
        throw new Error(t('auth.sso.errors.googleFailed'));
      }
      let idToken = signInResult.data.idToken;
      if (!idToken) {
        const tokens = await GoogleSignin.getTokens();
        idToken = tokens?.idToken;
      }
      if (!idToken) throw new Error('No ID token received from Google');

      await handleGoogleIdToken(idToken);
    } catch (err) {
      if (err?.code !== '-5' && err?.code !== 'SIGN_IN_CANCELLED') {
        setError(err.message || t('auth.sso.errors.googleFailed'));
      }
    } finally {
      setLoading(null);
    }
  }, [loading, setAuthData, onSuccess, t, handleGoogleIdToken]);

  const handleAppleIdToken = useCallback(async (idToken, fullName, expectedMacSession) => {
    if (!currentMacSignIn(expectedMacSession)) return;
    try {
      setLoading('apple');
      setError('');
      if (onIdentity) return await onIdentity({ provider: 'apple', idToken });
      const clientInfo = getClientInfo();
      const referralCode = await pendingReferral.read();
      const data = await postSSOLogin('/json/auth/login/apple', {
        idToken,
        fullName: fullName || undefined,
        clientInfo,
        ...(referralCode && { referralCode }),
      });
      if (!currentMacSignIn(expectedMacSession)) return;
      await handleSSOResponse(data, setAuthData, onSuccess, 'apple', { redirectAfterSuccess });
      if (data.tokens?.access) await pendingReferral.clear(referralCode);
    } catch (err) {
      setError(err.message || t('auth.sso.errors.appleFailed'));
    } finally {
      setLoading(null);
    }
  }, [setAuthData, onSuccess, onIdentity, t, currentMacSignIn, redirectAfterSuccess]);

  // Web: load Apple Sign-In JS
  useAppleWebSSO({ onResult: handleAppleIdToken });

  const handleApplePress = useCallback(async () => {
    if (loading) return;

    if (isWeb) {
      try {
        setLoading('apple');
        setError('');
        const response = await window.AppleID?.auth?.signIn();
        const idToken = response?.authorization?.id_token;
        if (!idToken) throw new Error('No identity token received from Apple');
        const fullName = response?.user?.name
          ? { givenName: response.user.name.firstName, familyName: response.user.name.lastName }
          : undefined;
        await handleAppleIdToken(idToken, fullName);
      } catch (err) {
        if (err?.error !== 'popup_closed_by_user') {
          setError(err.message || t('auth.sso.errors.appleFailed'));
        }
        setLoading(null);
      }
      return;
    }

    if (!isAppleNative) return;

    try {
      setLoading('apple');
      setError('');

      if (isMacOS) {
        const originalAccount = useAuthStore.getState();
        const expectedMacSession = { token: originalAccount.token, userId: originalAccount.user?.id };
        const credential = await NativeModules.MacAppleAuth?.signIn({ apiOrigin: API_HOST });
        if (!credential?.identityToken) throw new Error('No identity token received from Apple');
        await handleAppleIdToken(credential.identityToken, credential.fullName, expectedMacSession);
        return;
      }

      // iOS native: use expo-apple-authentication
      const AppleAuthentication = require('expo-apple-authentication');

      const isAvailable = await AppleAuthentication.isAvailableAsync();
      if (!isAvailable) {
        throw new Error('Apple Sign In is not available on this device');
      }

      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      });

      if (!credential.identityToken) throw new Error('No identity token received from Apple');

      await handleAppleIdToken(credential.identityToken, credential.fullName);
    } catch (err) {
      if (err?.code !== 'ERR_REQUEST_CANCELED') {
        setError(err.message || t('auth.sso.errors.appleFailed'));
      }
    } finally {
      setLoading(null);
    }
  }, [loading, handleAppleIdToken, t]);

  const googleLabel = mode === 'signup'
    ? t('auth.sso.signUpWithGoogle')
    : t('auth.sso.signInWithGoogle');

  const appleLabel = mode === 'signup'
    ? t('auth.sso.signUpWithApple')
    : t('auth.sso.signInWithApple');

  const showApple = (isAppleNative || isWeb) && (!providers || providers.includes('apple'));
  const showGoogle = !providers || providers.includes('google');

  return (
    <View style={styles.container}>
      {error ? <StatusContainer type="error" message={error} /> : null}

      {showGoogle && (isWeb ? (
        <GoogleWebSignInButton
          onIdToken={handleGoogleIdToken}
          onStart={() => setError('')}
          disabled={!!loading}
          mode={mode}
        />
      ) : (
        <Pressable
          style={[
            styles.button,
            {
              backgroundColor: colors.solid_color_primary,
              borderColor: colors.border_color_secondary,
            },
          ]}
          onPress={handleGooglePress}
          accessibilityRole="button"
          accessibilityLabel={googleLabel}
          accessibilityState={{ disabled: !!loading, busy: loading === 'google' }}
          disabled={!!loading}
        >
          {loading === 'google' ? (
            <ActivityIndicator size="small" color={colors.text_color_primary} />
          ) : (
            <>
              <GoogleLogo size={20} />
              <Text style={[styles.buttonText, { color: colors.text_color_primary }]}>
                {googleLabel}
              </Text>
            </>
          )}
        </Pressable>
      ))}

      {showApple && (
        <Pressable
          style={[
            styles.button,
            {
              backgroundColor: colors.solid_color_primary,
              borderColor: colors.border_color_secondary,
            },
          ]}
          onPress={handleApplePress}
          accessibilityRole="button"
          accessibilityLabel={appleLabel}
          accessibilityState={{ disabled: !!loading, busy: loading === 'apple' }}
          disabled={!!loading}
        >
          {loading === 'apple' ? (
            <ActivityIndicator size="small" color={colors.text_color_primary} />
          ) : (
            <>
              <AppleLogo size={20} color={colors.text_color_primary} />
              <Text style={[styles.buttonText, { color: colors.text_color_primary }]}>
                {appleLabel}
              </Text>
            </>
          )}
        </Pressable>
      )}

      {!onIdentity && (showGoogle || showApple) && (
        <View style={styles.dividerContainer}>
          <View style={[styles.dividerLine, { backgroundColor: colors.border_color_secondary }]} />
          <Text color="tertiary" size="small">{t('auth.sso.or')}</Text>
          <View style={[styles.dividerLine, { backgroundColor: colors.border_color_secondary }]} />
        </View>
      )}
    </View>
  );
};

export default React.memo(SSOButtons);
